/**
 * Trips Page
 * Shows all trips for the logged-in pilot
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTrips, Trip } from '../services/tripService';
import { useAuth } from '../contexts/AuthContext';
import { PlusIcon } from '@heroicons/react/24/outline';

export const TripsPage: React.FC = () => {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');

  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    loadTrips();
  }, [filterStatus]);

  const loadTrips = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTrips(filterStatus || undefined);
      setTrips(data);
    } catch (err) {
      setError('Failed to load trips. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING_REVIEW':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const formatStatus = (status: string) => {
    return status.replace('_', ' ').toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Your Trips</h1>
            <p className="mt-2 text-muted-foreground">
              Review and manage your assigned trips.
            </p>
          </div>
          <button
            onClick={() => navigate('/flights')}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Log Ad-hoc Flight
          </button>
        </div>

        {/* Filter */}
        <div className="mb-6 flex items-center space-x-4">
          <label htmlFor="status-filter" className="text-sm font-medium text-foreground">
            Filter by status:
          </label>
          <select
            id="status-filter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="block w-64 px-3 py-2 bg-card border border-border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary text-foreground"
          >
            <option value="">All Trips</option>
            <option value="PENDING_REVIEW">Pending Review</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <p className="text-red-800 dark:text-red-400">{error}</p>
            <button
              onClick={loadTrips}
              className="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Trips List */}
        {!loading && !error && (
          <>
            {trips.length === 0 ? (
              <div className="bg-card rounded-lg shadow border border-border p-12 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-muted-foreground"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-foreground">No trips found</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {filterStatus
                    ? `No trips with status "${formatStatus(filterStatus)}"`
                    : 'You don\'t have any trips assigned yet.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {trips.map((trip) => (
                  <div
                    key={trip.id}
                    className="bg-card rounded-lg shadow hover:shadow-md transition-shadow border border-border"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* Trip Header */}
                          <div className="flex items-center space-x-3 mb-3">
                            <h3 className="text-xl font-semibold text-foreground">
                              Trip {trip.qNumber}
                            </h3>
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(trip.status)}`}
                            >
                              {formatStatus(trip.status)}
                            </span>
                          </div>

                          {/* Trip Details */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            {/* Dates */}
                            <div>
                              <p className="text-muted-foreground mb-1">Dates</p>
                              <p className="font-medium text-foreground">
                                {formatDate(trip.startDate)} - {formatDate(trip.endDate)}
                              </p>
                            </div>

                            {/* Aircraft */}
                            <div>
                              <p className="text-muted-foreground mb-1">Aircraft</p>
                              <p className="font-medium text-foreground">
                                {trip.tailNumber || 'N/A'}
                                {trip.aircraftType && (
                                  <span className="text-muted-foreground text-xs ml-2">
                                    ({trip.aircraftType})
                                  </span>
                                )}
                              </p>
                            </div>

                            {/* Crew */}
                            <div>
                              <p className="text-muted-foreground mb-1">Crew</p>
                              <p className="font-medium text-foreground">
                                {trip.crewMembers.length > 0
                                  ? trip.crewMembers
                                      .map((cm) => `${cm.user.firstName} ${cm.user.lastName}`)
                                      .join(', ')
                                  : 'Not assigned'}
                              </p>
                            </div>
                          </div>

                          {/* Flights Summary */}
                          <div className="mt-4 pt-4 border-t border-border">
                            <p className="text-sm text-muted-foreground">
                              {trip.flights.length} flight leg{trip.flights.length !== 1 ? 's' : ''}
                              {trip.flights.some(f => f.status === 'PENDING_REVIEW') && (
                                <span className="ml-2 text-yellow-600 dark:text-yellow-400 font-medium">
                                  • {trip.flights.filter(f => f.status === 'PENDING_REVIEW').length} pending review
                                </span>
                              )}
                            </p>
                          </div>

                          {/* Action Buttons */}
                          <div className="mt-4 flex gap-3">
                            {/* Complete Flights Button - Pilots only */}
                            {user?.role === 'pilot' && (trip.status === 'PENDING_REVIEW' || trip.status === 'IN_PROGRESS') && (
                              <button
                                onClick={() => navigate(`/trips/${trip.id}/wizard`)}
                                className="flex-1 py-3 px-4 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-lg transition-colors"
                              >
                                Complete Flights
                              </button>
                            )}

                            {/* View Details Button */}
                            <button
                              onClick={() => navigate(`/trips/${trip.id}`)}
                              className="py-3 px-4 bg-muted hover:bg-muted/80 text-foreground font-medium rounded-lg transition-colors"
                            >
                              View Details
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
